import React, { useState, Fragment } from 'react';
import { ArrowLeft, Share2, FileText, Bold, Italic, Underline, Strikethrough, AlignLeft, AlignCenter, AlignRight, AlignJustify, List, ListOrdered, Indent, Outdent, Link, Image, Table, Code, Quote, Undo, Redo, ChevronDown, Type, Palette, Search, ZoomIn, Grid3x3, MessageSquare, Tag, Maximize2, Building2, Plus, Check, Download, PenLine, ClipboardCheck, X } from 'lucide-react';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
interface Business {
  id: string;
  name: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  hstNumber: string;
  registrationNumber: string;
  isDefault?: boolean;
}
interface DocumentEditorProps {
  onMenuClick?: () => void;
  onNavigateToAICopilot?: () => void;
  onBack?: () => void;
  prefillData?: {
    business?: any;
    familyMember?: any;
    employee?: any;
  };
  templateTitle?: string;
  userBusinesses?: Business[];
  userFamilyMembers?: any[];
  userEmployees?: any[];
}
export function DocumentEditor({
  onMenuClick,
  onNavigateToAICopilot,
  onBack,
  prefillData,
  templateTitle,
  userBusinesses: propBusinesses,
  userFamilyMembers,
  userEmployees
}: DocumentEditorProps) {
  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const steps = [{
    id: 1,
    title: 'Company Details',
    description: 'Basic company information'
  }, {
    id: 2,
    title: 'Contact Information',
    description: 'Phone and website'
  }, {
    id: 3,
    title: 'Address Details',
    description: 'Company location'
  }, {
    id: 4,
    title: 'Review & Finish',
    description: 'Review your information'
  }];
  // Mock user businesses - simulating data from MyAccount
  const [userBusinesses] = useState<Business[]>(propBusinesses || [{
    id: '1',
    name: 'Maple Leaf Restaurant',
    address: '123 Queen Street West',
    city: 'Toronto',
    province: 'Ontario',
    postalCode: 'M5H 2N2',
    hstNumber: '123456789RT0001',
    registrationNumber: 'ON-12345',
    isDefault: true
  }, {
    id: '2',
    name: 'Northern Lights Consulting',
    address: '456 Yonge Street',
    city: 'Toronto',
    province: 'Ontario',
    postalCode: 'M4Y 1X5',
    hstNumber: '987654321RT0002',
    registrationNumber: 'ON-67890',
    isDefault: false
  }, {
    id: '3',
    name: 'Pacific Coast Trading Co.',
    address: '789 Granville Street',
    city: 'Vancouver',
    province: 'British Columbia',
    postalCode: 'V6Z 1K3',
    hstNumber: '456789123RT0003',
    registrationNumber: 'BC-24680',
    isDefault: false
  }]);
  // Get default business or first business, or use prefilled business
  const defaultBusiness = prefillData?.business || userBusinesses.find(b => b.isDefault) || userBusinesses[0];
  // State for selected business
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(defaultBusiness || null);
  const [showBusinessDropdown, setShowBusinessDropdown] = useState(false);
  const [showCompanyNameDropdown, setShowCompanyNameDropdown] = useState(false);
  const [companyName, setCompanyName] = useState(defaultBusiness?.name || '');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [address, setAddress] = useState(defaultBusiness?.address || '');
  const [website, setWebsite] = useState('');
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const handleFinish = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowDocumentPreview(true);
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else if (onBack) {
      onBack();
    } else {
      console.log('Back clicked');
    }
  };
  const handleSelectBusiness = (business: Business) => {
    setSelectedBusiness(business);
    setCompanyName(business.name);
    setAddress(business.address);
    setShowBusinessDropdown(false);
  };
  const handleAddNewBusiness = () => {
    setShowBusinessDropdown(false);
    // Clear form for new business
    setSelectedBusiness(null);
    setCompanyName('');
    setAddress('');
    setPhoneNumber('');
    setWebsite('');
  };
  return <div className="flex flex-col h-screen bg-aliceBlue" data-id="element-1679">
      <DashboardHeader categories={['Business', 'HR']} onMenuClick={onMenuClick} onNavigateToAICopilot={onNavigateToAICopilot} data-id="element-1680" />

      <main className="flex-1 overflow-hidden flex" data-id="element-1681">
        {/* Left Sidebar - Form - Much Wider */}
        <div className="min-w-[33%] max-w-[50%] bg-white border-r border-borderColor flex flex-col" data-id="element-1682">
          {/* Header */}
          <div className="p-6 border-b border-borderColor" data-id="element-1683">
            <div className="flex items-center gap-2 text-secondary mb-4" data-id="element-1684">
              <button onClick={handleBack} className="p-1 hover:bg-aliceBlue rounded transition-colors" data-id="element-1685">
                <ArrowLeft className="w-4 h-4" data-id="element-1686" />
              </button>
              <span className="text-sm" data-id="element-1687">
                {templateTitle || 'AODA ACCESSIBILITY STANDARDS POLICY (6)'}
              </span>
            </div>
          </div>

          {/* Form Content */}
          <div className="flex-1 overflow-y-auto p-6" data-id="element-1688">
            {/* Progress Bar - Minimal Style - Single Row */}
            <div className="mb-8" data-id="element-1689">
              <div className="flex items-center gap-3 w-full" data-id="element-1690">
                {/* Progress blocks container - takes full width */}
                <div className="flex-1 flex gap-2" data-id="element-1691">
                  {steps.map(step => {
                  const isComplete = currentStep > step.id;
                  const isCurrent = currentStep === step.id;
                  const allComplete = currentStep > totalSteps;
                  const isLastStep = currentStep === totalSteps;
                  return <div key={step.id} className={`flex-1 h-3 rounded-sm transition-colors duration-300 ${allComplete ? 'bg-turquoise' : isLastStep && (isComplete || isCurrent) ? 'bg-turquoise' : isComplete ? 'bg-violetBlue' : isCurrent ? 'bg-violetBlue' : 'bg-gray-300'}`} data-id="element-1692" />;
                })}
                </div>
                {/* Checkmark + Ready label on last step */}
                {currentStep >= totalSteps && <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-1693">
                    <div className="w-5 h-5 rounded-full border-2 border-turquoise flex items-center justify-center" data-id="element-1694">
                      <Check className="w-3 h-3 text-turquoise" data-id="element-1695" />
                    </div>
                  </div>}
              </div>
            </div>

            {/* Step Title */}
            <h3 className="text-base font-semibold text-primary mb-2" data-id="element-1696">
              {steps[currentStep - 1].title}
            </h3>
            <p className="text-sm text-secondary mb-6" data-id="element-1697">
              {steps[currentStep - 1].description}
            </p>

            {/* Step Content */}
            <div className="space-y-5" data-id="element-1698">
              {/* Step 1: Company Details */}
              {currentStep === 1 && <div data-id="element-1699">
                  <label className="block text-sm font-medium text-secondary mb-2" data-id="element-1700">
                    Company name
                  </label>
                  {userBusinesses.length > 1 ? <div className="relative" data-id="element-1701">
                      <div className="relative" data-id="element-1702">
                        <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} onFocus={() => setShowCompanyNameDropdown(true)} className="w-full px-4 py-2.5 pr-10 border border-borderColor rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise" placeholder="Select or enter company name" data-id="element-1703" />
                        <button onClick={() => setShowCompanyNameDropdown(!showCompanyNameDropdown)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-aliceBlue rounded transition-colors" data-id="element-1704">
                          <ChevronDown className="w-4 h-4 text-secondary" data-id="element-1705" />
                        </button>
                      </div>

                      {showCompanyNameDropdown && <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-borderColor rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto" data-id="element-1706">
                          {userBusinesses.map(business => <button key={business.id} onClick={() => {
                    setCompanyName(business.name);
                    setAddress(business.address);
                    setSelectedBusiness(business);
                    setShowCompanyNameDropdown(false);
                  }} className="w-full px-4 py-2.5 text-left hover:bg-aliceBlue transition-colors text-sm text-primary border-b border-borderColor last:border-b-0" data-id="element-1707">
                              {business.name}
                            </button>)}
                          <button onClick={() => {
                    setCompanyName('');
                    setAddress('');
                    setSelectedBusiness(null);
                    setShowCompanyNameDropdown(false);
                  }} className="w-full px-4 py-2.5 text-left hover:bg-aliceBlue transition-colors flex items-center gap-2 text-violetBlue font-medium text-sm" data-id="element-1708">
                            <Plus className="w-4 h-4" data-id="element-1709" />
                            Add New Business
                          </button>
                        </div>}
                    </div> : <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} className="w-full px-4 py-2.5 border border-borderColor rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise" placeholder="Enter company name" data-id="element-1710" />}
                </div>}

              {/* Step 2: Contact Information */}
              {currentStep === 2 && <>
                  <div data-id="element-1711">
                    <label className="block text-sm font-medium text-secondary mb-2" data-id="element-1712">
                      Phone Number
                    </label>
                    <input type="tel" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} className="w-full px-4 py-2.5 border border-borderColor rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise" placeholder="Enter phone number" data-id="element-1713" />
                  </div>

                  <div data-id="element-1714">
                    <label className="block text-sm font-medium text-secondary mb-2" data-id="element-1715">
                      Website
                    </label>
                    <input type="url" value={website} onChange={e => setWebsite(e.target.value)} className="w-full px-4 py-2.5 border border-borderColor rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise" placeholder="Enter website URL" data-id="element-1716" />
                  </div>
                </>}

              {/* Step 3: Address Details */}
              {currentStep === 3 && <div data-id="element-1717">
                  <label className="block text-sm font-medium text-secondary mb-2" data-id="element-1718">
                    Address
                  </label>
                  <textarea value={address} onChange={e => setAddress(e.target.value)} rows={3} className="w-full px-4 py-2.5 border border-borderColor rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise resize-none" placeholder="Enter address" data-id="element-1719" />
                </div>}

              {/* Step 4: Review */}
              {currentStep === 4 && <div className="space-y-4" data-id="element-1720">
                  <div className="bg-aliceBlue rounded-lg p-4" data-id="element-1721">
                    <h4 className="text-sm font-semibold text-primary mb-3" data-id="element-1722">
                      Review Your Information
                    </h4>
                    <div className="space-y-2 text-sm" data-id="element-1723">
                      <div className="flex justify-between" data-id="element-1724">
                        <span className="text-secondary" data-id="element-1725">Company Name:</span>
                        <span className="text-primary font-medium" data-id="element-1726">
                          {companyName || 'Not provided'}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-1727">
                        <span className="text-secondary" data-id="element-1728">Phone Number:</span>
                        <span className="text-primary font-medium" data-id="element-1729">
                          {phoneNumber || 'Not provided'}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-1730">
                        <span className="text-secondary" data-id="element-1731">Website:</span>
                        <span className="text-primary font-medium" data-id="element-1732">
                          {website || 'Not provided'}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-1733">
                        <span className="text-secondary" data-id="element-1734">Address:</span>
                        <span className="text-primary font-medium" data-id="element-1735">
                          {address || 'Not provided'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="p-6 border-t border-borderColor flex gap-3" data-id="element-1736">
            <button onClick={handleBack} className="flex-1 px-4 py-2.5 border border-borderColor text-primary rounded-lg hover:bg-aliceBlue transition-colors text-sm font-medium" data-id="element-1737">
              {currentStep === 1 ? 'Cancel' : 'Previous'}
            </button>
            <button onClick={handleFinish} className="flex-1 px-4 py-2.5 rounded-lg transition-colors text-sm font-medium bg-violetBlue hover:bg-violetBlue/90 text-white" data-id="element-1738">
              {currentStep === totalSteps ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>

        {/* Right Side - Document Editor */}
        <div className="flex-1 flex flex-col bg-white" data-id="element-1739">
          {/* Toolbar */}
          <div className="border-b border-borderColor" data-id="element-1740">
            {/* Top Toolbar Row */}
            <div className="flex items-center gap-1 px-4 py-2 border-b border-borderColor" data-id="element-1741">
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1742">
                <Undo className="w-4 h-4" data-id="element-1743" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1744">
                <Redo className="w-4 h-4" data-id="element-1745" />
              </button>

              <div className="w-px h-6 bg-borderColor mx-1" data-id="element-1746" />

              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1747">
                <Bold className="w-4 h-4" data-id="element-1748" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1749">
                <Italic className="w-4 h-4" data-id="element-1750" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1751">
                <Underline className="w-4 h-4" data-id="element-1752" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1753">
                <Strikethrough className="w-4 h-4" data-id="element-1754" />
              </button>

              <div className="w-px h-6 bg-borderColor mx-1" data-id="element-1755" />

              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1756">
                <AlignLeft className="w-4 h-4" data-id="element-1757" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1758">
                <AlignCenter className="w-4 h-4" data-id="element-1759" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1760">
                <AlignRight className="w-4 h-4" data-id="element-1761" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1762">
                <AlignJustify className="w-4 h-4" data-id="element-1763" />
              </button>

              <div className="w-px h-6 bg-borderColor mx-1" data-id="element-1764" />

              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1765">
                <List className="w-4 h-4" data-id="element-1766" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1767">
                <ListOrdered className="w-4 h-4" data-id="element-1768" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1769">
                <Outdent className="w-4 h-4" data-id="element-1770" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1771">
                <Indent className="w-4 h-4" data-id="element-1772" />
              </button>

              <div className="w-px h-6 bg-borderColor mx-1" data-id="element-1773" />

              <button className="flex items-center gap-1 px-3 py-1.5 hover:bg-aliceBlue rounded transition-colors text-secondary text-sm" data-id="element-1774">
                Normal
                <ChevronDown className="w-3 h-3" data-id="element-1775" />
              </button>

              <button className="flex items-center gap-1 px-3 py-1.5 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1776">
                <Type className="w-4 h-4" data-id="element-1777" />
                <ChevronDown className="w-3 h-3" data-id="element-1778" />
              </button>

              <button className="flex items-center gap-1 px-3 py-1.5 hover:bg-aliceBlue rounded transition-colors text-secondary text-sm" data-id="element-1779">
                Arial
                <ChevronDown className="w-3 h-3" data-id="element-1780" />
              </button>

              <div className="w-px h-6 bg-borderColor mx-1" data-id="element-1781" />

              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1782">
                <Palette className="w-4 h-4" data-id="element-1783" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1784">
                <Link className="w-4 h-4" data-id="element-1785" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1786">
                <Image className="w-4 h-4" data-id="element-1787" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1788">
                <Search className="w-4 h-4" data-id="element-1789" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1790">
                <ZoomIn className="w-4 h-4" data-id="element-1791" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1792">
                <Grid3x3 className="w-4 h-4" data-id="element-1793" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1794">
                <Table className="w-4 h-4" data-id="element-1795" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1796">
                <MessageSquare className="w-4 h-4" data-id="element-1797" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1798">
                <Tag className="w-4 h-4" data-id="element-1799" />
              </button>
              <button className="p-2 hover:bg-aliceBlue rounded transition-colors text-secondary" data-id="element-1800">
                <Maximize2 className="w-4 h-4" data-id="element-1801" />
              </button>
            </div>
          </div>

          {/* Document Content */}
          <div className="flex-1 overflow-y-auto p-8 bg-gray-50" data-id="element-1802">
            <div className="max-w-4xl mx-auto bg-white shadow-sm min-h-full p-12" data-id="element-1803">
              <div className="prose max-w-none" data-id="element-1804">
                <h1 className="text-center text-xl font-bold text-primary mb-8" data-id="element-1805">
                  ACCESSIBILITY FOR ONTARIANS WITH DISABILITIES ACT, 2005 POLICY
                </h1>

                <h2 className="text-base font-bold text-primary mt-6 mb-3" data-id="element-1806">
                  Section 1 Statement of Commitment
                </h2>

                <p className="text-sm text-primary leading-relaxed mb-4" data-id="element-1807">
                  The goal of the Accessibility for Ontarians with Disabilities
                  Act, 2005 ("AODA") is to create a more accessible Ontario by
                  identifying and, to the extent possible, preventing and
                  eliminating barriers experienced by persons with disabilities.
                  There are five standards set out under the AODA, all of which
                  will be implemented by the year 2025. These standards relate
                  to customer service, transportation, information and
                  communications, built environment and employment (collectively
                  the "Integrated Accessibility Standards" or "IAS").
                </p>

                <p className="text-sm text-primary leading-relaxed mb-4" data-id="element-1808">
                  The Company* is committed to meeting the objectives and
                  requirements of the AODA and IAS, including the accessibility
                  needs of persons with disabilities with respect to the
                  Company's programs, services and facilities, in a timely
                  manner.
                </p>

                <h2 className="text-base font-bold text-primary mt-6 mb-3" data-id="element-1809">
                  Section 2 Definitions
                </h2>

                <p className="text-sm text-primary leading-relaxed mb-2" data-id="element-1810">
                  <strong data-id="element-1811">Accessible format(s)</strong>: means formats that are
                  an alternative to standard print and are accessible to persons
                  with disabilities. Accessible formats may include, but are not
                  limited to, large print, recorded audio and electronic
                  formats, and other formats usable by persons with
                  disabilities.
                </p>

                <p className="text-sm text-primary leading-relaxed mb-2" data-id="element-1812">
                  <strong data-id="element-1813">Assistive device(s)</strong>: means any device used to
                  assist a person in performing a particular task(s) or to aid
                  that person in activities of daily living. This can include a
                  wheelchair, screen reader, listening device or cane.
                </p>

                <p className="text-sm text-primary leading-relaxed mb-2" data-id="element-1814">
                  <strong data-id="element-1815">Communication support(s)</strong>: means supports that
                  persons with disabilities may need to access information.
                  Communication supports may include, but are not limited to,
                  captioning, alternative and augmentative supports, plain
                  language, sign language and other supports that facilitate
                  effective communications.
                </p>

                <p className="text-sm text-primary leading-relaxed mb-4" data-id="element-1816">
                  <strong data-id="element-1817">Disability</strong> means:
                </p>

                <ol className="list-decimal list-inside text-sm text-primary space-y-2 mb-4 ml-4" data-id="element-1818">
                  <li data-id="element-1819">
                    Any degree of physical disability, infirmity, malformation
                    or disfigurement that is caused by bodily injury, birth
                    defect or illness and, without limiting the generality of
                    the foregoing, includes diabetes mellitus, epilepsy, a brain
                    injury, any degree of paralysis, amputation, lack of
                    coordination, blindness or visual impediment, deafness or
                    hearing impediment, muteness or speech impediment, or
                    physical reliance on a guide dog or other animal, or on a
                    wheelchair or other remedial appliance or device;
                  </li>
                  <li data-id="element-1820">
                    A condition of mental impairment or developmental
                    disability;
                  </li>
                  <li data-id="element-1821">
                    A learning disability, or a dysfunction in one or more of
                    the processes involved in understanding or using symbols or
                    spoken language;
                  </li>
                  <li data-id="element-1822">A mental disorder; or</li>
                  <li data-id="element-1823">
                    An injury or disability for which benefits were claimed or
                    received under the insurance plan established under the
                    Workplace Safety and Insurance Act, 1997.
                  </li>
                </ol>

                <p className="text-sm text-primary leading-relaxed mb-2" data-id="element-1824">
                  <strong data-id="element-1825">Employee(s)</strong>: means any employee or volunteer
                  of the Company.
                </p>

                <p className="text-sm text-primary leading-relaxed mb-2" data-id="element-1826">
                  <strong data-id="element-1827">Guide dog(s)</strong>: means a highly-trained working
                  dog that has been trained at one of the facilities listed in
                  Ontario Regulation 58 under the Blind Persons' Rights Act,
                  1990 to provide mobility, safety and increased independence
                  for people who are blind.
                </p>

                <p className="text-sm text-primary leading-relaxed mb-4" data-id="element-1828">
                  <strong data-id="element-1829">Service animal(s)</strong>: an animal is a service
                  animal for a person with a disability if:
                </p>

                <ul className="list-disc list-inside text-sm text-primary space-y-2 mb-4 ml-4" data-id="element-1830">
                  <li data-id="element-1831">
                    It is readily apparent that the animal is used by the person
                    for reasons relating to his or her disability; or
                  </li>
                  <li data-id="element-1832">
                    The person provides a letter from a regulated health
                    professional confirming that the person requires the animal
                    for reasons relating to the disability.
                  </li>
                </ul>

                <p className="text-sm text-primary leading-relaxed mb-4" data-id="element-1833">
                  <strong data-id="element-1834">Support person(s)</strong>: means an individual hired
                  or chosen by a person with a disability to provide services in
                  communication, mobility, personal care, medical needs or with
                  access to goods or services. Medical needs may include, but
                  are not limited to, monitoring an individual's health or
                  providing medical support by being available in the event of a
                  seizure.
                </p>

                <h2 className="text-base font-bold text-primary mt-6 mb-3" data-id="element-1835">
                  Section 3 Customer Service Standard
                </h2>

                <h3 className="text-sm font-bold text-primary mt-4 mb-2" data-id="element-1836">
                  Purpose
                </h3>

                <p className="text-sm text-primary leading-relaxed mb-4" data-id="element-1837">
                  The Company is committed to providing an equal opportunity to
                  all of our customers. The objective of this policy is to
                  ensure we meet the requirements of the customer service
                  standard and promote its underlying core principles.
                </p>

                <h3 className="text-sm font-bold text-primary mt-4 mb-2" data-id="element-1838">
                  Scope
                </h3>

                <p className="text-sm text-primary leading-relaxed mb-4" data-id="element-1839">
                  All employees, contractors and agents who work on behalf of
                  the Company and deal with members of the public or other third
                  parties are expected to conduct themselves in accordance with
                  this policy.
                </p>

                <h3 className="text-sm font-bold text-primary mt-4 mb-2" data-id="element-1840">
                  Core Principles
                </h3>

                <p className="text-sm text-primary leading-relaxed mb-2" data-id="element-1841">
                  The Company will make every effort to ensure that this policy
                  and related practices and procedures are consistent with the
                  following four core principles:
                </p>

                <p className="text-sm text-primary leading-relaxed mb-2 italic" data-id="element-1842">
                  <strong data-id="element-1843">Dignity:</strong> Persons with disabilities must be
                  treated as valued customers as deserving of service as any
                  other customer.
                </p>

                <p className="text-sm text-primary leading-relaxed mb-2 italic" data-id="element-1844">
                  <strong data-id="element-1845">Equality of Opportunity:</strong> Persons with
                  disabilities should be given an equal opportunity to obtain,
                  use and benefit from the Company's goods and services.
                </p>

                <p className="text-sm text-primary leading-relaxed mb-4 italic" data-id="element-1846">
                  <strong data-id="element-1847">Integration:</strong> Wherever possible, persons with
                  disabilities should benefit from the Company's goods and
                  services in the same place and in the same or similar manner
                  as any other customer. In circumstances where integration does
                  not serve the needs of persons with disabilities, goods and
                  services will, to the extent possible, be provided in another
                  way that takes into account the person's individual needs.
                </p>
              </div>
            </div>
          </div>

          {/* Language Selector */}
          <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-white border border-borderColor rounded-lg shadow-lg p-2" data-id="element-1848">
            <button className="px-3 py-1 text-sm font-medium text-destructive border-b-2 border-destructive" data-id="element-1849">
              CA
            </button>
            <button className="px-3 py-1 text-sm font-medium text-secondary hover:text-primary" data-id="element-1850">
              EN
            </button>
          </div>
        </div>
      </main>

      {/* Document Preview Modal */}
      {showDocumentPreview && <div className="fixed inset-0 z-50 flex items-center justify-center" data-id="element-1851">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowDocumentPreview(false)} data-id="element-1852" />

          {/* Modal */}
          <div className="relative w-[95vw] max-w-6xl h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-id="element-1853">
            {/* Top Action Bar */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-borderColor bg-gray-50" data-id="element-1854">
              <div className="flex items-center gap-2" data-id="element-1855">
                <FileText className="w-5 h-5 text-violetBlue" data-id="element-1856" />
                <h3 className="text-sm font-semibold text-primary" data-id="element-1857">
                  {templateTitle || 'AODA ACCESSIBILITY STANDARDS POLICY'}
                </h3>
              </div>

              <div className="flex items-center gap-1" data-id="element-1858">
                <button onClick={() => console.log('Sign')} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-secondary hover:text-primary hover:bg-aliceBlue rounded-lg transition-colors" data-id="element-1859">
                  <PenLine className="w-4 h-4" data-id="element-1860" />
                  <span className="hidden sm:inline" data-id="element-1861">Sign</span>
                </button>
                <button onClick={() => console.log('Share')} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-secondary hover:text-primary hover:bg-aliceBlue rounded-lg transition-colors" data-id="element-1862">
                  <Share2 className="w-4 h-4" data-id="element-1863" />
                  <span className="hidden sm:inline" data-id="element-1864">Share</span>
                </button>
                <button onClick={() => console.log('Request Review')} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-secondary hover:text-primary hover:bg-aliceBlue rounded-lg transition-colors" data-id="element-1865">
                  <ClipboardCheck className="w-4 h-4" data-id="element-1866" />
                  <span className="hidden sm:inline" data-id="element-1867">Request a Review</span>
                </button>
                <button onClick={() => console.log('Download')} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-violetBlue hover:bg-violetBlue/90 rounded-lg transition-colors" data-id="element-1868">
                  <Download className="w-4 h-4" data-id="element-1869" />
                  <span className="hidden sm:inline" data-id="element-1870">Download</span>
                </button>

                <div className="w-px h-6 bg-borderColor mx-2" data-id="element-1871" />

                <button onClick={() => setShowDocumentPreview(false)} className="p-2 text-secondary hover:text-primary hover:bg-aliceBlue rounded-lg transition-colors" data-id="element-1872">
                  <X className="w-4 h-4" data-id="element-1873" />
                </button>
              </div>
            </div>

            {/* PDF-like Document View */}
            <div className="flex-1 overflow-y-auto bg-gray-100 p-8" data-id="element-1874">
              <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-sm min-h-[1100px] p-16 border border-gray-200" data-id="element-1875">
                <div className="prose max-w-none" data-id="element-1876">
                  <h1 className="text-center text-xl font-bold text-primary mb-8" data-id="element-1877">
                    ACCESSIBILITY FOR ONTARIANS WITH DISABILITIES ACT, 2005
                    POLICY
                  </h1>

                  {companyName && <p className="text-sm text-primary leading-relaxed mb-6 text-center" data-id="element-1878">
                      <strong data-id="element-1879">Prepared for:</strong> {companyName}
                      {address && <> - {address}</>}
                    </p>}

                  <h2 className="text-base font-bold text-primary mt-6 mb-3" data-id="element-1880">
                    Section 1 Statement of Commitment
                  </h2>

                  <p className="text-sm text-primary leading-relaxed mb-4" data-id="element-1881">
                    The goal of the Accessibility for Ontarians with
                    Disabilities Act, 2005 ("AODA") is to create a more
                    accessible Ontario by identifying and, to the extent
                    possible, preventing and eliminating barriers experienced by
                    persons with disabilities. There are five standards set out
                    under the AODA, all of which will be implemented by the year
                    2025. These standards relate to customer service,
                    transportation, information and communications, built
                    environment and employment (collectively the "Integrated
                    Accessibility Standards" or "IAS").
                  </p>

                  <p className="text-sm text-primary leading-relaxed mb-4" data-id="element-1882">
                    {companyName || 'The Company'} is committed to meeting the
                    objectives and requirements of the AODA and IAS, including
                    the accessibility needs of persons with disabilities with
                    respect to the Company's programs, services and facilities,
                    in a timely manner.
                  </p>

                  <h2 className="text-base font-bold text-primary mt-6 mb-3" data-id="element-1883">
                    Section 2 Definitions
                  </h2>

                  <p className="text-sm text-primary leading-relaxed mb-2" data-id="element-1884">
                    <strong data-id="element-1885">Accessible format(s)</strong>: means formats that
                    are an alternative to standard print and are accessible to
                    persons with disabilities. Accessible formats may include,
                    but are not limited to, large print, recorded audio and
                    electronic formats, and other formats usable by persons with
                    disabilities.
                  </p>

                  <p className="text-sm text-primary leading-relaxed mb-2" data-id="element-1886">
                    <strong data-id="element-1887">Assistive device(s)</strong>: means any device used
                    to assist a person in performing a particular task(s) or to
                    aid that person in activities of daily living. This can
                    include a wheelchair, screen reader, listening device or
                    cane.
                  </p>

                  <p className="text-sm text-primary leading-relaxed mb-2" data-id="element-1888">
                    <strong data-id="element-1889">Communication support(s)</strong>: means supports
                    that persons with disabilities may need to access
                    information. Communication supports may include, but are not
                    limited to, captioning, alternative and augmentative
                    supports, plain language, sign language and other supports
                    that facilitate effective communications.
                  </p>

                  <h2 className="text-base font-bold text-primary mt-6 mb-3" data-id="element-1890">
                    Section 3 Customer Service Standard
                  </h2>

                  <p className="text-sm text-primary leading-relaxed mb-4" data-id="element-1891">
                    {companyName || 'The Company'} is committed to providing an
                    equal opportunity to all of our customers. The objective of
                    this policy is to ensure we meet the requirements of the
                    customer service standard and promote its underlying core
                    principles.
                  </p>

                  <p className="text-sm text-primary leading-relaxed mb-2 italic" data-id="element-1892">
                    <strong data-id="element-1893">Dignity:</strong> Persons with disabilities must be
                    treated as valued customers as deserving of service as any
                    other customer.
                  </p>

                  <p className="text-sm text-primary leading-relaxed mb-2 italic" data-id="element-1894">
                    <strong data-id="element-1895">Equality of Opportunity:</strong> Persons with
                    disabilities should be given an equal opportunity to obtain,
                    use and benefit from the Company's goods and services.
                  </p>

                  <p className="text-sm text-primary leading-relaxed mb-4 italic" data-id="element-1896">
                    <strong data-id="element-1897">Integration:</strong> Wherever possible, persons
                    with disabilities should benefit from the Company's goods
                    and services in the same place and in the same or similar
                    manner as any other customer.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}